import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {AlertTriangle, CheckCircle2, Calendar, ArrowRight, Briefcase, Clock, Plane, Shield} from 'lucide-react';
import BookingModal from '../components/BookingModal';

const BusinessTravel = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const healthRisks = [
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Jet Lag & Fatigue",
      desc: "Time zone changes and tight schedules impact performance and health."
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: "Last-Minute Planning",
      desc: "Short notice trips leave little time for comprehensive health preparation."
    },
    {
      icon: <Plane className="w-6 h-6" />,
      title: "Frequent Travel Risks",
      desc: "Regular international travel increases cumulative exposure to health risks."
    }
  ];

  const vaccinesDiscuss = [
    { name: "Routine Boosters (Tdap)", priority: "Ensure Up-to-Date", note: "Tetanus, diphtheria, pertussis every 10 years" },
    { name: "Hepatitis A", priority: "Recommended", note: "Protection for frequent international dining" },
    { name: "Hepatitis B", priority: "Consider", note: "For frequent travellers or healthcare work" },
    { name: "Typhoid", priority: "For High-Risk Regions", note: "South Asia, Southeast Asia, parts of Africa/Latin America" },
    { name: "Influenza (Flu)", priority: "Highly Recommended", note: "Annual flu shot, especially during flu season travel" },
    { name: "COVID-19", priority: "Recommended", note: "Stay current with boosters per destination requirements" },
    { name: "Measles/Mumps/Rubella (MMR)", priority: "Ensure Up-to-Date", note: "Especially if born after 1970" },
    { name: "Japanese Encephalitis", priority: "For Rural Asia", note: "Consider if frequent rural travel in endemic areas" }
  ];

  const medications = [
    "Travellers' diarrhea treatment kit (ciprofloxacin or azithromycin)",
    "Melatonin or sleep aids for jet lag management",
    "Oral rehydration salts for dehydration",
    "Pain relievers (ibuprofen, acetaminophen)",
    "Prescription medications in carry-on with copies of prescriptions",
    "Hand sanitizer and disinfecting wipes"
  ];

  const businessTravelTips = [
    {
      icon: <Clock className="w-5 h-5" />,
      tip: "Manage Jet Lag",
      detail: "Adjust sleep schedule 2-3 days before travel. Stay hydrated. Use melatonin wisely."
    },
    {
      icon: <Shield className="w-5 h-5" />,
      tip: "Food Safety",
      detail: "Choose reputable restaurants. Avoid buffets in high-risk countries. Stick to bottled water."
    },
    {
      icon: <Briefcase className="w-5 h-5" />,
      tip: "Travel Health Insurance",
      detail: "Ensure coverage includes medical evacuation and emergency care abroad."
    },
    {
      icon: <Plane className="w-5 h-5" />,
      tip: "Frequent Flyer Health",
      detail: "Stay active during flights. Compression socks for long hauls. DVT prevention."
    }
  ];

  const commonDestinations = [
    { region: "Asia-Pacific", cities: ["Singapore", "Hong Kong", "Tokyo", "Shanghai", "Seoul"] },
    { region: "Europe", cities: ["London", "Frankfurt", "Paris", "Amsterdam", "Zurich"] },
    { region: "Middle East", cities: ["Dubai", "Abu Dhabi", "Doha", "Riyadh", "Tel Aviv"] },
    { region: "Americas", cities: ["New York", "São Paulo", "Mexico City", "Buenos Aires", "Santiago"] }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-navy-900">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-blue-700 via-indigo-700 to-blue-900 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10" />
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/1181406/pexels-photo-1181406.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Business Travel"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <div className="inline-block px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm font-medium mb-6">
              Business Travel • Corporate • Frequent Flyers
            </div>
            <h1 className="text-4xl md:text-6xl font-heading font-bold text-white mb-6">
              Business & Short-Stay Travel Health
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Fast, efficient health planning for time-conscious business travellers. Stay protected without disrupting your schedule.
            </p>
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Book Business Travel Consultation
              <ArrowRight size={20} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Business Travel Advantages */}
      <section className="py-16 bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-heading font-bold text-navy-900 dark:text-white mb-8 text-center">
            Why Business Travellers Choose Haven
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 bg-gradient-to-br from-blue-50 to-white dark:from-navy-800 dark:to-navy-900 rounded-2xl border border-gray-100 dark:border-white/5 text-center"
            >
              <Clock className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
              <h4 className="text-lg font-bold text-navy-900 dark:text-white mb-2">24-48 Hour Turnaround</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Fast consultations and same-day prescriptions</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-6 bg-gradient-to-br from-blue-50 to-white dark:from-navy-800 dark:to-navy-900 rounded-2xl border border-gray-100 dark:border-white/5 text-center"
            >
              <Briefcase className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
              <h4 className="text-lg font-bold text-navy-900 dark:text-white mb-2">Evening Appointments</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Virtual consults around your work schedule</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="p-6 bg-gradient-to-br from-blue-50 to-white dark:from-navy-800 dark:to-navy-900 rounded-2xl border border-gray-100 dark:border-white/5 text-center"
            >
              <Shield className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
              <h4 className="text-lg font-bold text-navy-900 dark:text-white mb-2">Corporate Plans</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Group rates for frequent travellers available</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="p-6 bg-gradient-to-br from-blue-50 to-white dark:from-navy-800 dark:to-navy-900 rounded-2xl border border-gray-100 dark:border-white/5 text-center"
            >
              <Plane className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
              <h4 className="text-lg font-bold text-navy-900 dark:text-white mb-2">Digital Records</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Access vaccination history anytime, anywhere</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Common Destinations */}
      <section className="py-16 bg-gray-50 dark:bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-heading font-bold text-navy-900 dark:text-white mb-8 text-center">
            Common Business Travel Hubs
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {commonDestinations.map((dest, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-5 bg-white dark:bg-navy-900 rounded-xl border border-gray-100 dark:border-white/5"
              >
                <h4 className="text-lg font-bold text-navy-900 dark:text-white mb-3">{dest.region}</h4>
                <ul className="space-y-1">
                  {dest.cities.map((city, i) => (
                    <li key={i} className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                      {city}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Health Considerations */}
      <section className="py-20 bg-white dark:bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-4">
              Health Considerations for Business Travellers
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Even short trips require smart health planning. Don't let preventable illness disrupt your business goals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {healthRisks.map((risk, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-gray-50 dark:bg-navy-800 rounded-2xl border border-gray-100 dark:border-white/5"
              >
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-xl flex items-center justify-center text-blue-600 dark:text-blue-400 mb-4">
                  {risk.icon}
                </div>
                <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-3">{risk.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{risk.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vaccines & Medications */}
      <section className="py-20 bg-gray-50 dark:bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Vaccines */}
            <div>
              <h2 className="text-3xl font-heading font-bold text-navy-900 dark:text-white mb-6">
                Vaccines to Discuss with Your Clinician
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Focus on routine boosters and destination-specific risks for efficient protection.
              </p>
              <div className="space-y-4">
                {vaccinesDiscuss.map((vaccine, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 bg-white dark:bg-navy-900 rounded-xl border border-gray-100 dark:border-white/5 hover:border-cyan-500/30 transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-bold text-navy-900 dark:text-white">{vaccine.name}</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${
                        vaccine.priority.includes("Highly")
                          ? "bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400"
                          : vaccine.priority.includes("Recommended")
                          ? "bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }`}>
                        {vaccine.priority}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{vaccine.note}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Medications & Tips */}
            <div>
              <h2 className="text-3xl font-heading font-bold text-navy-900 dark:text-white mb-6">
                Essential Travel Medications
              </h2>
              <ul className="space-y-3 mb-8">
                {medications.map((med, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                    <span className="text-gray-700 dark:text-gray-300">{med}</span>
                  </motion.li>
                ))}
              </ul>

              <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-4">Business Travel Best Practices</h3>
              <div className="space-y-3">
                {businessTravelTips.map((tip, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-white dark:bg-navy-900 rounded-lg">
                    <div className="w-8 h-8 bg-cyan-100 dark:bg-cyan-900/20 rounded-lg flex items-center justify-center text-cyan-600 dark:text-cyan-400 shrink-0">
                      {tip.icon}
                    </div>
                    <div>
                      <div className="font-bold text-navy-900 dark:text-white text-sm">{tip.tip}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{tip.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When to Book */}
      <section className="py-20 bg-gradient-to-br from-navy-900 to-navy-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Calendar className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Flexible Scheduling for Busy Professionals
            </h2>
            <p className="text-gray-300 text-lg">
              We understand your schedule is tight. We offer fast, efficient consultations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-cyan-400 mb-2">Last Minute</div>
              <div className="text-white font-medium mb-2">Same-Week Booking</div>
              <div className="text-sm text-gray-400">Virtual consults available within 24-48 hours</div>
            </div>
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-blue-400 mb-2">2-3 Weeks</div>
              <div className="text-white font-medium mb-2">Ideal for Most Trips</div>
              <div className="text-sm text-gray-400">Time for routine boosters and prescriptions</div>
            </div>
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-indigo-400 mb-2">Anytime</div>
              <div className="text-white font-medium mb-2">Evening & Weekend</div>
              <div className="text-sm text-gray-400">Appointments outside business hours available</div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Book Your Business Travel Consultation
              <ArrowRight size={20} />
            </button>
            <p className="text-gray-400 text-sm mt-4">
              Virtual consultations • $60 CAD • Fast turnaround • Corporate plans available
            </p>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </div>
  );
};

export default BusinessTravel;
